/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { Select } from 'antd'

interface Props {
  ageFilters?: string[] | null
  genderFilters?: string[] | null
  dayOfWeekFilters?: string[] | null
  onAgeChange: (value: string | null) => void
  onGenderChange: (value: string | null) => void
  onDayOfWeekChange: (value: string | null) => void
}

/**
 * Age / gender / day-of-week narrowing for the division grid.
 *
 * Each control appears only when its list holds more than one value — a filter
 * with a single option cannot narrow anything and just costs a row.
 */
const DivisionFilters = ({
  ageFilters,
  genderFilters,
  dayOfWeekFilters,
  onAgeChange,
  onGenderChange,
  onDayOfWeekChange,
}: Props) => {
  // 'N/A' is a real value in the payload's division_gender, but it is not a
  // gender anyone would filter by.
  const genders = (genderFilters ?? []).filter(
    (gender) => gender && gender !== 'N/A'
  )
  const ages = ageFilters ?? []
  const days = dayOfWeekFilters ?? []

  const showAge = ages.length > 1
  const showGender = genders.length > 1
  const showDay = days.length > 1

  if (!showAge && !showGender && !showDay) {
    return null
  }

  const toOptions = (values: string[], placeholder: string) => [
    { value: '', label: capitalizeWords(placeholder) },
    ...values.map((value) => ({ value, label: value })),
  ]

  return (
    <div css={rowStyle}>
      {showAge ? (
        <Select
          defaultValue=''
          css={selectStyle}
          variant='borderless'
          onChange={onAgeChange}
          options={toOptions(ages, 'Select an Age')}
        />
      ) : null}
      {showGender ? (
        <Select
          defaultValue=''
          css={selectStyle}
          variant='borderless'
          onChange={onGenderChange}
          options={toOptions(genders, 'Select a Gender')}
        />
      ) : null}
      {showDay ? (
        <Select
          defaultValue=''
          css={selectStyle}
          variant='borderless'
          onChange={onDayOfWeekChange}
          options={toOptions(days, 'Select a Day of Week')}
        />
      ) : null}
    </div>
  )
}

export default DivisionFilters

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    .ant-select {
      width: 100%;
    }
  }
`

const selectStyle = css`
  border-radius: 8px;
  border: 1px solid ${colors.STORM_GRAY};
  background: ${colors.VERY_DARK_GRAYISH_BLUE};
  display: flex;
  overflow: hidden;
  color: ${colors.WHITE};
  text-overflow: ellipsis;
  min-width: 150px;
  height: 40px;
`
